import { cn } from 'lib/utils'
import { Loader2, Plus, X } from 'lucide-react'
import * as React from 'react'

interface BaseTileProps {
  /** Tile size — defaults to 80px square. Pass `lg` for 120px. */
  size?: 'default' | 'lg'
  className?: string
}

interface ImageTileProps extends BaseTileProps {
  /** Image URL or blob. */
  src: string
  /** Optional alt for a11y. */
  alt?: string
  /** True while the upload is still in flight; renders an overlay spinner. */
  uploading?: boolean
  /** Optional remove handler — renders an X button on hover when set. */
  onRemove?: () => void
  /** Optional click — renders the tile as a clickable card. */
  onClick?: () => void
}

interface UploadTileProps extends BaseTileProps {
  /** True while the upload is in flight; spinner replaces the plus icon. */
  uploading?: boolean
  /** Optional accept hint forwarded to the file input. Defaults to images. */
  accept?: string
  /** Multi-file upload? */
  multiple?: boolean
  onUpload: (files: FileList) => void
}

const sizeClasses = {
  default: 'h-20 w-20',
  lg: 'h-[120px] w-[120px]',
} as const

/**
 * T3 image tile (filled). Used in image library / picker grids: brand kit
 * inspiration images, composer image picker, etc. Square card with rounded
 * corners, hairline border, optional remove affordance and uploading state.
 *
 * Spec: playbook/design-system/components.md#image-picker-tile.
 */
export const ImagePickerTile: React.FC<ImageTileProps> = ({
  src,
  alt = '',
  uploading,
  onRemove,
  onClick,
  size = 'default',
  className,
}) => {
  return (
    <div
      className={cn(
        'group relative shrink-0 overflow-hidden rounded-[10px] border border-line bg-bg-2',
        sizeClasses[size],
        onClick && 'cursor-pointer hover:border-line-bright',
        className
      )}
      onClick={onClick}
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className={cn('h-full w-full object-cover', uploading && 'opacity-40')}
      />
      {uploading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 size={16} className="animate-spin text-acid" />
        </div>
      )}
      {onRemove && !uploading && (
        <button
          type="button"
          onClick={e => {
            e.stopPropagation()
            onRemove()
          }}
          aria-label="Remove image"
          className="absolute top-1 right-1 z-10 rounded-full border border-line-bright bg-bg-3 p-0.5 text-paper-dim opacity-0 transition-opacity group-hover:opacity-100 hover:text-paper"
        >
          <X size={12} strokeWidth={2.5} />
        </button>
      )}
    </div>
  )
}

/**
 * Empty add-tile that opens a file picker. Pair with `ImagePickerTile` for
 * the "library" pattern — fill grid with existing images, last tile is the
 * uploader.
 */
export const ImageUploadTile: React.FC<UploadTileProps> = ({
  uploading,
  accept = 'image/*',
  multiple,
  onUpload,
  size = 'default',
  className,
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null)

  return (
    <label
      className={cn(
        'flex shrink-0 cursor-pointer items-center justify-center rounded-[10px] border-2 border-dashed border-line-bright text-paper-dim transition-colors hover:border-acid hover:text-acid',
        sizeClasses[size],
        uploading && 'pointer-events-none opacity-60',
        className
      )}
    >
      {uploading ? (
        <Loader2 size={16} className="animate-spin" />
      ) : (
        <Plus size={18} strokeWidth={2} />
      )}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={e => {
          if (e.target.files?.length) onUpload(e.target.files)
          // Reset so re-uploading the same file fires onChange.
          if (inputRef.current) inputRef.current.value = ''
        }}
        className="pointer-events-none fixed top-0 left-0 h-px w-px opacity-0"
      />
    </label>
  )
}
